/**
 * SEO resolution + scoring.
 *
 * - resolveSeo(path)  -> the generated metadata for any route (static or dynamic)
 * - buildMetaTags()   -> the raw <meta> tags to inject
 * - analyzeSeo(path)  -> a pass/fail checklist and a 0-100 score
 */
import { staticSeo, type SeoEntry } from "@/config/seo";
import { areaDetails } from "@/mocks/areas";

export const SITE = "https://laharipackersandmovers.in";
/** Public, always-reachable social share image (1200 x 630). */
export const SOCIAL_IMAGE =
  "https://readdy.ai/api/search-image?query=Wide%20cinematic%20banner%20of%20a%20professional%20moving%20crew%20in%20uniform%20loading%20neatly%20wrapped%20cardboard%20boxes%20and%20furniture%20into%20a%20clean%20modern%20moving%20truck%20parked%20outside%20a%20bright%20Bangalore%20residential%20apartment%20building%2C%20warm%20golden%20hour%20light%2C%20deep%20green%20and%20cream%20color%20palette%2C%20clear%20uncluttered%20composition%2C%20photorealistic%20premium%20relocation%20marketing%20photography%2C%20no%20text%20letters%20or%20logos&width=1200&height=630&seq=lahari-social-cover-2026&orientation=landscape";
export const SOCIAL_IMAGE_WIDTH = 1200;
export const SOCIAL_IMAGE_HEIGHT = 630;
export const SOCIAL_IMAGE_ALT = "Lahari Packers and Movers Bangalore - home and office shifting service";
export const DEFAULT_IMAGE = SOCIAL_IMAGE;

/** Turn any image path into a full web address (social crawlers cannot resolve relative paths). */
const toAbsoluteUrl = (image: string): string => {
  if (/^https?:\/\//i.test(image)) return image;
  return `${SITE}${image.startsWith("/") ? "" : "/"}${image}`;
};

const normalise = (path: string): string => {
  const clean = path.split("?")[0].split("#")[0].replace(/\/+$/, "");
  return clean === "" ? "/" : clean;
};

/** Resolve the generated SEO entry for any pathname. */
export const resolveSeo = (pathname: string): SeoEntry => {
  const path = normalise(pathname);

  const direct = staticSeo[path];
  if (direct) return direct;

  const areaMatch = path.match(/^\/areas-we-serve\/([^/]+)$/);
  if (areaMatch) {
    const area = areaDetails.find((item) => item.slug === areaMatch[1]);
    if (area) {
      return {
        title: `Packers and Movers in ${area.name}, Bangalore | Lahari`,
        description: `Local packers and movers in ${area.name}, Bangalore for same-day house, flat and office shifting. Get a free moving quote from Lahari.`,
        keywords: `packers and movers ${area.name}, local shifting ${area.name}, movers in ${area.name} bangalore`,
      };
    }
  }

  return staticSeo["/"];
};

const clip = (text: string, max: number): string => {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
};

export interface MetaTag {
  attr: "name" | "property";
  key: string;
  content: string;
}

/** Build the full set of head tags for an entry (description is trimmed to ~155 chars). */
export const buildMetaTags = (entry: SeoEntry, pathname: string): MetaTag[] => {
  const path = normalise(pathname);
  const url = path === "/" ? `${SITE}/` : `${SITE}${path}`;
  const description = clip(entry.description, 155);
  const image = toAbsoluteUrl(entry.image || DEFAULT_IMAGE);
  return [
    { attr: "name", key: "description", content: description },
    { attr: "name", key: "keywords", content: entry.keywords || "" },
    { attr: "property", key: "og:title", content: entry.title },
    { attr: "property", key: "og:description", content: description },
    { attr: "property", key: "og:type", content: entry.type || "website" },
    { attr: "property", key: "og:url", content: url },
    { attr: "property", key: "og:site_name", content: "Lahari Packers and Movers, Bangalore" },
    { attr: "property", key: "og:locale", content: "en_IN" },
    { attr: "property", key: "og:image", content: image },
    { attr: "property", key: "og:image:width", content: String(SOCIAL_IMAGE_WIDTH) },
    { attr: "property", key: "og:image:height", content: String(SOCIAL_IMAGE_HEIGHT) },
    { attr: "property", key: "og:image:alt", content: SOCIAL_IMAGE_ALT },
    { attr: "name", key: "twitter:card", content: "summary_large_image" },
    { attr: "name", key: "twitter:title", content: entry.title },
    { attr: "name", key: "twitter:description", content: description },
    { attr: "name", key: "twitter:image", content: image },
    { attr: "name", key: "twitter:image:alt", content: SOCIAL_IMAGE_ALT },
  ];
};

export interface SeoCheck {
  label: string;
  pass: boolean;
  detail: string;
}

export interface SeoReport {
  path: string;
  entry: SeoEntry;
  checks: SeoCheck[];
  score: number;
}

/** Score a route's generated metadata against common on-page SEO best practice. */
export const analyzeSeo = (pathname: string): SeoReport => {
  const path = normalise(pathname);
  const entry = resolveSeo(path);
  const description = clip(entry.description, 155);
  const titleLen = entry.title.length;
  const descLen = description.length;
  const keywordCount = entry.keywords ? entry.keywords.split(",").filter((k) => k.trim()).length : 0;

  const checks: SeoCheck[] = [
    {
      label: "Title present & well-sized",
      pass: titleLen >= 30 && titleLen <= 60,
      detail: `${titleLen} characters (aim 30–60)`,
    },
    {
      label: "Meta description well-sized",
      pass: descLen >= 120 && descLen <= 155,
      detail: `${descLen} characters (aim 120–155)`,
    },
    {
      label: "Target keywords (3+)",
      pass: keywordCount >= 3,
      detail: keywordCount > 0 ? `${keywordCount} keywords` : "no keywords set",
    },
    {
      label: "Canonical URL",
      pass: true,
      detail: path === "/" ? `${SITE}/` : `${SITE}${path}`,
    },
    {
      label: "Open Graph tags",
      pass: true,
      detail: "og:title, og:description, og:url, og:image",
    },
    {
      label: "Twitter card",
      pass: true,
      detail: "summary_large_image",
    },
  ];

  const passed = checks.filter((check) => check.pass).length;
  return {
    path,
    entry,
    checks,
    score: Math.round((passed / checks.length) * 100),
  };
};

/** Every static route in the central config, plus every dynamic area route. */
export const listSeoRoutes = (): string[] => {
  const staticPaths = Object.keys(staticSeo);
  const areaPaths = areaDetails.map((area) => `/areas-we-serve/${area.slug}`);
  return [...staticPaths, ...areaPaths];
};